import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/Auth/AuthProvider';
import { Plus, FileText, Calendar, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { quickEntryService } from '@/services/quickEntryService';

interface Assessment {
  id: string;
  supplier_id: string | null;
  assessment_type: string;
  assessment_date: string;
  assessor_name: string | null;
  overall_score: number | null;
  environmental_score: number | null;
  social_score: number | null;
  governance_score: number | null;
  findings: string | null;
  recommendations: string | null;
  action_items: string | null;
  follow_up_date: string | null;
  status: string | null;
  suppliers?: {
    supplier_name: string;
  };
}

interface AssessmentManagerProps {
  onUpdate: () => void;
}

export const AssessmentManager = ({ onUpdate }: AssessmentManagerProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAddingAssessment, setIsAddingAssessment] = useState(false);
  const [editingAssessmentId, setEditingAssessmentId] = useState<string | null>(null);
  const [viewAssessment, setViewAssessment] = useState<any | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdminStatus, setCheckingAdminStatus] = useState(true);
  const [userType, setUserType] = useState<'individual' | 'organization_admin' | 'super_admin' | null>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [customQuestions, setCustomQuestions] = useState<any[]>([]);
  const [newQuestion, setNewQuestion] = useState({ label: '', type: 'text', options: '', required: false });
  const [newAssessment, setNewAssessment] = useState({
    supplier_id: '',
    assessment_type: 'annual',
    assessment_date: new Date().toISOString().split('T')[0],
    assessor_name: '',
    overall_score: '',
    environmental_score: '',
    social_score: '',
    governance_score: '',
    findings: '',
    recommendations: '',
    action_items: '',
    follow_up_date: '',
    status: 'pending',
    // Additional ESG fields
    has_ems: false,
    policies_environmental: false,
    policies_human_rights: false,
    policies_anti_corruption: false,
    data_privacy_policy: false,
    whistleblower_mechanism: false,
    sbti_committed: false,
    ghg_disclosed: false,
    ecovadis_score: '',
    cdp_score: '',
    scope1_emissions: '',
    scope2_emissions: '',
    scope3_emissions: '',
    energy_consumption_mwh: '',
    water_withdrawal_m3: '',
    waste_generated_tons: '',
    incidents_count: '',
    incidents_details: '',
    cap_in_place: false,
    cap_due_date: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState<string | null>(null);

  // Fetch assessments from Supabase with supplier names
  const { data: assessments, isLoading } = useQuery({
    queryKey: ['supply-chain-assessments', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data: assessmentsData, error } = await supabase
        .from('supply_chain_assessments')
        .select('*')
        .eq('user_id', user.id)
        .order('assessment_date', { ascending: false });

      if (error) throw error;

      // Fetch supplier names for each assessment
      const assessmentsWithSuppliers = await Promise.all(
        (assessmentsData || []).map(async (assessment) => {
          let supplierName = 'Unknown Supplier';

          if (assessment.supplier_id) {
            const { data: supplier } = await supabase
              .from('suppliers')
              .select('supplier_name')
              .eq('id', assessment.supplier_id)
              .single();

            if (supplier) {
              supplierName = supplier.supplier_name;
            }
          }

          return {
            ...assessment,
            suppliers: { supplier_name: supplierName }
          };
        })
      );

      return assessmentsWithSuppliers as Assessment[];
    },
    enabled: !!user?.id,
  });

  // Fetch suppliers from Supabase
  const { data: suppliers } = useQuery({
    queryKey: ['suppliers-select', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('suppliers')
        .select('id, supplier_name, esg_assessment_score')
        .eq('user_id', user.id)
        .order('supplier_name', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Fetch templates from Supabase
  const { data: fetchedTemplates } = useQuery({
    queryKey: ['assessment-templates', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('assessment_templates')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        // Table might not exist yet
        return [];
      }

      return data || [];
    },
    enabled: !!user?.id && isAddingAssessment,
  });

  useEffect(() => {
    if (fetchedTemplates) {
      setTemplates(fetchedTemplates);
    }
  }, [fetchedTemplates]);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) return;
      try {
        setCheckingAdminStatus(true);
        const { data: adminUser } = await supabase
          .from('admin_users')
          .select('role, is_super_admin')
          .eq('user_id', user.id)
          .single();
        if (adminUser && (adminUser.is_super_admin || adminUser.role === 'admin')) {
          setIsAdmin(true);
          setUserType('super_admin');
          return;
        }
        const { data: orgMember } = await supabase
          .from('organization_members')
          .select('role, can_approve_data')
          .eq('user_id', user.id)
          .single();
        if (orgMember && (orgMember.role === 'admin' || orgMember.can_approve_data)) {
          setIsAdmin(true);
          setUserType('organization_admin');
          return;
        }
        const { data: individualUser } = await supabase
          .from('individual_users')
          .select('id')
          .eq('user_id', user.id)
          .single();
        if (individualUser) {
          setIsAdmin(false);
          setUserType('individual');
          return;
        }
        setIsAdmin(false);
        setUserType(null);
      } catch {
        setIsAdmin(false);
        setUserType(null);
      } finally {
        setCheckingAdminStatus(false);
      }
    };
    if (user?.id) checkAdminStatus();
  }, [user?.id]);

  const createAssessmentMutation = useMutation({
    mutationFn: async (assessmentData: any) => {
      setIsSubmitting(true);
      try {
        const result = await quickEntryService.createAssessment(assessmentData);
        if (!result.success) throw new Error(result.error || 'Failed to create assessment');
        return result.data;
      } finally {
        setIsSubmitting(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supply-chain-assessments'] });
      toast({
        title: "Assessment Created",
        description: "Supply chain assessment has been created successfully.",
      });
      setIsAddingAssessment(false);
      resetForm();
      onUpdate();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create assessment. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateAssessmentMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      setIsSubmitting(true);
      try {
        const { error } = await supabase
          .from('supply_chain_assessments')
          .update({
            ...data,
            updated_at: new Date().toISOString()
          })
          .eq('id', id);

        if (error) throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supply-chain-assessments'] });
      toast({
        title: "Assessment Updated",
        description: "Supply chain assessment has been updated successfully.",
      });
      setIsAddingAssessment(false);
      setEditingAssessmentId(null);
      resetForm();
      onUpdate();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update assessment. Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteAssessmentMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('supply_chain_assessments')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supply-chain-assessments'] });
      toast({
        title: "Assessment Deleted",
        description: "Supply chain assessment has been deleted successfully.",
      });
      onUpdate();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete assessment. Please try again.",
        variant: "destructive",
      });
    },
  });

  const saveTemplateMutation = useMutation({
    mutationFn: async ({ name, templateData }: { name: string; templateData: any }) => {
      const result = await quickEntryService.saveAssessmentTemplate(name, templateData);
      if (!result.success) throw new Error(result.error || 'Failed to save template');
      return result.data;
    },
    onSuccess: (data, variables) => {
      toast({
        title: "Template Saved",
        description: `Template '${variables.name}' saved successfully.`,
      });
      queryClient.invalidateQueries({ queryKey: ['assessment-templates'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save template. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const assessmentData = {
      ...newAssessment,
      overall_score: newAssessment.overall_score ? parseFloat(newAssessment.overall_score) : null,
      environmental_score: newAssessment.environmental_score ? parseFloat(newAssessment.environmental_score) : null,
      social_score: newAssessment.social_score ? parseFloat(newAssessment.social_score) : null,
      governance_score: newAssessment.governance_score ? parseFloat(newAssessment.governance_score) : null,
      supplier_id: newAssessment.supplier_id || null,
      follow_up_date: newAssessment.follow_up_date || null,
      ecovadis_score: newAssessment.ecovadis_score ? parseFloat(newAssessment.ecovadis_score) : null,
      cdp_score: newAssessment.cdp_score || null,
      scope1_emissions: newAssessment.scope1_emissions ? parseFloat(newAssessment.scope1_emissions) : null,
      scope2_emissions: newAssessment.scope2_emissions ? parseFloat(newAssessment.scope2_emissions) : null,
      scope3_emissions: newAssessment.scope3_emissions ? parseFloat(newAssessment.scope3_emissions) : null,
      energy_consumption_mwh: newAssessment.energy_consumption_mwh ? parseFloat(newAssessment.energy_consumption_mwh) : null,
      water_withdrawal_m3: newAssessment.water_withdrawal_m3 ? parseFloat(newAssessment.water_withdrawal_m3) : null,
      waste_generated_tons: newAssessment.waste_generated_tons ? parseFloat(newAssessment.waste_generated_tons) : null,
      incidents_count: newAssessment.incidents_count ? parseInt(newAssessment.incidents_count) : null,
      incidents_details: newAssessment.incidents_details || null,
      cap_due_date: newAssessment.cap_due_date || null,
      custom_fields: customQuestions.map((q) => ({ label: q.label, type: q.type, value: q.value ?? null }))
    };

    if (editingAssessmentId) {
      updateAssessmentMutation.mutate({ id: editingAssessmentId, data: assessmentData });
    } else {
      createAssessmentMutation.mutate(assessmentData);
    }
  };

  const resetForm = () => {
    setNewAssessment({
      supplier_id: '',
      assessment_type: 'annual',
      assessment_date: new Date().toISOString().split('T')[0],
      assessor_name: '',
      overall_score: '',
      environmental_score: '',
      social_score: '',
      governance_score: '',
      findings: '',
      recommendations: '',
      action_items: '',
      follow_up_date: '',
      status: 'pending',
      has_ems: false,
      policies_environmental: false,
      policies_human_rights: false,
      policies_anti_corruption: false,
      data_privacy_policy: false,
      whistleblower_mechanism: false,
      sbti_committed: false,
      ghg_disclosed: false,
      ecovadis_score: '',
      cdp_score: '',
      scope1_emissions: '',
      scope2_emissions: '',
      scope3_emissions: '',
      energy_consumption_mwh: '',
      water_withdrawal_m3: '',
      waste_generated_tons: '',
      incidents_count: '',
      incidents_details: '',
      cap_in_place: false,
      cap_due_date: '',
    });
    setCustomQuestions([]);
    setEditingAssessmentId(null);
    setIsAddingAssessment(false);
  };

  const handleEditAssessment = (assessment: Assessment) => {
    const { id, suppliers, ...assessmentData } = assessment as any;

    setNewAssessment({
      supplier_id: assessmentData.supplier_id || '',
      assessment_type: assessmentData.assessment_type || 'annual',
      assessment_date: assessmentData.assessment_date || new Date().toISOString().split('T')[0],
      assessor_name: assessmentData.assessor_name || '',
      overall_score: assessmentData.overall_score?.toString() || '',
      environmental_score: assessmentData.environmental_score?.toString() || '',
      social_score: assessmentData.social_score?.toString() || '',
      governance_score: assessmentData.governance_score?.toString() || '',
      findings: assessmentData.findings || '',
      recommendations: assessmentData.recommendations || '',
      action_items: assessmentData.action_items || '',
      follow_up_date: assessmentData.follow_up_date || '',
      status: assessmentData.status || 'pending',
      has_ems: assessmentData.has_ems || false,
      policies_environmental: assessmentData.policies_environmental || false,
      policies_human_rights: assessmentData.policies_human_rights || false,
      policies_anti_corruption: assessmentData.policies_anti_corruption || false,
      data_privacy_policy: assessmentData.data_privacy_policy || false,
      whistleblower_mechanism: assessmentData.whistleblower_mechanism || false,
      sbti_committed: assessmentData.sbti_committed || false,
      ghg_disclosed: assessmentData.ghg_disclosed || false,
      ecovadis_score: assessmentData.ecovadis_score?.toString() || '',
      cdp_score: assessmentData.cdp_score || '',
      scope1_emissions: assessmentData.scope1_emissions?.toString() || '',
      scope2_emissions: assessmentData.scope2_emissions?.toString() || '',
      scope3_emissions: assessmentData.scope3_emissions?.toString() || '',
      energy_consumption_mwh: assessmentData.energy_consumption_mwh?.toString() || '',
      water_withdrawal_m3: assessmentData.water_withdrawal_m3?.toString() || '',
      waste_generated_tons: assessmentData.waste_generated_tons?.toString() || '',
      incidents_count: assessmentData.incidents_count?.toString() || '',
      incidents_details: assessmentData.incidents_details || '',
      cap_in_place: assessmentData.cap_in_place || false,
      cap_due_date: assessmentData.cap_due_date || '',
    });

    // Load custom fields if they exist
    if (assessmentData.custom_fields && Array.isArray(assessmentData.custom_fields)) {
      setCustomQuestions(assessmentData.custom_fields.map((field: any) => ({
        label: field.label,
        type: field.type,
        value: field.value,
        options: field.options || ''
      })));
    }

    setEditingAssessmentId(assessment.id);
    setIsAddingAssessment(true);
  };

  const handleDeleteAssessment = (id: string) => {
    setAssessmentToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (assessmentToDelete) {
      deleteAssessmentMutation.mutate(assessmentToDelete);
      setIsDeleteDialogOpen(false);
      setAssessmentToDelete(null);
    }
  };

  const getStatusBadge = (status: string | null) => {
    const statusColors = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
    };
    return statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return <div className="flex justify-center p-8">Loading assessments...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Supply Chain Assessments</h3>
        <Dialog open={isAddingAssessment} onOpenChange={setIsAddingAssessment}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              New Assessment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingAssessmentId ? 'Edit Assessment' : 'Create New Assessment'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Template controls */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <Label htmlFor="template">Template</Label>
                  <Select
                    onValueChange={async (templateId) => {
                      const template = templates.find((t) => t.id === templateId);
                      if (template && template.template_data) {
                        // Apply template data
                        const templateData = template.template_data;
                        setNewAssessment((prev) => ({ ...prev, ...templateData }));

                        // Apply custom questions if they exist
                        if (templateData.custom_fields) {
                          setCustomQuestions(templateData.custom_fields.map((field: any) => ({
                            ...field,
                            value: field.value || ''
                          })));
                        }
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select saved template (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      const name = prompt('Save current fields as template. Enter template name:');
                      if (!name) return;

                      const templateData = {
                        ...newAssessment,
                        custom_fields: customQuestions.map((q) => ({
                          label: q.label,
                          type: q.type,
                          value: q.value ?? '',
                          options: q.options || ''
                        }))
                      };

                      saveTemplateMutation.mutate({ name, templateData });
                    }}
                    disabled={saveTemplateMutation.isPending}
                  >
                    {saveTemplateMutation.isPending ? 'Saving...' : 'Save as Template'}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="supplier">Supplier</Label>
                  <Select
                    value={newAssessment.supplier_id}
                    onValueChange={(value) => {
                      setNewAssessment({ ...newAssessment, supplier_id: value });

                      // Auto-populate ESG score if available
                      const selectedSupplier = suppliers?.find(s => s.id === value);
                      if (selectedSupplier?.esg_assessment_score) {
                        setNewAssessment(prev => ({
                          ...prev,
                          overall_score: selectedSupplier.esg_assessment_score.toString()
                        }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers?.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.supplier_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="assessment_type">Assessment Type</Label>
                  <Select
                    value={newAssessment.assessment_type}
                    onValueChange={(value) => setNewAssessment({ ...newAssessment, assessment_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="initial">Initial</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                      <SelectItem value="audit">Audit</SelectItem>
                      <SelectItem value="incident_based">Incident Based</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="assessment_date">Assessment Date</Label>
                  <Input
                    id="assessment_date"
                    type="date"
                    value={newAssessment.assessment_date}
                    onChange={(e) => setNewAssessment({ ...newAssessment, assessment_date: e.target.value })}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <Label htmlFor="assessor_name">Assessor Name</Label>
                  <Input
                    id="assessor_name"
                    value={newAssessment.assessor_name}
                    onChange={(e) => setNewAssessment({ ...newAssessment, assessor_name: e.target.value })}
                    placeholder="Enter assessor name"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <Label htmlFor="overall_score">Overall Score (0-100)</Label>
                  <Input
                    id="overall_score"
                    type="number"
                    min="0"
                    max="100"
                    value={newAssessment.overall_score}
                    onChange={(e) => setNewAssessment({ ...newAssessment, overall_score: e.target.value })}
                    placeholder="0-100"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <Label htmlFor="environmental_score">Environmental Score</Label>
                  <Input
                    id="environmental_score"
                    type="number"
                    min="0"
                    max="100"
                    value={newAssessment.environmental_score}
                    onChange={(e) => setNewAssessment({ ...newAssessment, environmental_score: e.target.value })}
                    placeholder="0-100"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <Label htmlFor="social_score">Social Score</Label>
                  <Input
                    id="social_score"
                    type="number"
                    min="0"
                    max="100"
                    value={newAssessment.social_score}
                    onChange={(e) => setNewAssessment({ ...newAssessment, social_score: e.target.value })}
                    placeholder="0-100"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <Label htmlFor="governance_score">Governance Score</Label>
                  <Input
                    id="governance_score"
                    type="number"
                    min="0"
                    max="100"
                    value={newAssessment.governance_score}
                    onChange={(e) => setNewAssessment({ ...newAssessment, governance_score: e.target.value })}
                    placeholder="0-100"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="findings">Key Findings</Label>
                <Textarea
                  id="findings"
                  value={newAssessment.findings}
                  onChange={(e) => setNewAssessment({ ...newAssessment, findings: e.target.value })}
                  placeholder="Document key findings from the assessment"
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <Label htmlFor="recommendations">Recommendations</Label>
                <Textarea
                  id="recommendations"
                  value={newAssessment.recommendations}
                  onChange={(e) => setNewAssessment({ ...newAssessment, recommendations: e.target.value })}
                  placeholder="Provide recommendations for improvement"
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <Label htmlFor="action_items">Action Items</Label>
                <Textarea
                  id="action_items"
                  value={newAssessment.action_items}
                  onChange={(e) => setNewAssessment({ ...newAssessment, action_items: e.target.value })}
                  placeholder="List specific action items to be completed"
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="follow_up_date">Follow-up Date</Label>
                  <Input
                    id="follow_up_date"
                    type="date"
                    value={newAssessment.follow_up_date}
                    onChange={(e) => setNewAssessment({ ...newAssessment, follow_up_date: e.target.value })}
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={newAssessment.status}
                    onValueChange={(value) => setNewAssessment({ ...newAssessment, status: value })}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Governance & Policy Controls */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={newAssessment.has_ems}
                    onCheckedChange={(checked) => setNewAssessment({ ...newAssessment, has_ems: checked })}
                    disabled={isSubmitting}
                  />
                  <Label>Environmental Management System (ISO 14001)</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={newAssessment.policies_environmental}
                    onCheckedChange={(checked) => setNewAssessment({ ...newAssessment, policies_environmental: checked })}
                    disabled={isSubmitting}
                  />
                  <Label>Environmental Policy</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={newAssessment.policies_human_rights}
                    onCheckedChange={(checked) => setNewAssessment({ ...newAssessment, policies_human_rights: checked })}
                    disabled={isSubmitting}
                  />
                  <Label>Human Rights Policy</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={newAssessment.policies_anti_corruption}
                    onCheckedChange={(checked) => setNewAssessment({ ...newAssessment, policies_anti_corruption: checked })}
                    disabled={isSubmitting}
                  />
                  <Label>Anti-Corruption Policy</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={newAssessment.data_privacy_policy}
                    onCheckedChange={(checked) => setNewAssessment({ ...newAssessment, data_privacy_policy: checked })}
                    disabled={isSubmitting}
                  />
                  <Label>Data Privacy Policy</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={newAssessment.whistleblower_mechanism}
                    onCheckedChange={(checked) => setNewAssessment({ ...newAssessment, whistleblower_mechanism: checked })}
                    disabled={isSubmitting}
                  />
                  <Label>Whistleblower Mechanism</Label>
                </div>
              </div>

              {/* Climate & Disclosure */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={newAssessment.sbti_committed}
                    onCheckedChange={(checked) => setNewAssessment({ ...newAssessment, sbti_committed: checked })}
                    disabled={isSubmitting}
                  />
                  <Label>SBTi Committed</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={newAssessment.ghg_disclosed}
                    onCheckedChange={(checked) => setNewAssessment({ ...newAssessment, ghg_disclosed: checked })}
                    disabled={isSubmitting}
                  />
                  <Label>GHG Emissions Disclosed</Label>
                </div>
                <div>
                  <Label>EcoVadis Score</Label>
                  <Input
                    value={newAssessment.ecovadis_score}
                    onChange={(e) => setNewAssessment({ ...newAssessment, ecovadis_score: e.target.value })}
                    placeholder="0-100"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label>CDP Score</Label>
                  <Select
                    value={newAssessment.cdp_score}
                    onValueChange={(value) => setNewAssessment({ ...newAssessment, cdp_score: value })}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {['A', 'A-', 'B', 'B-', 'C', 'C-', 'D', 'D-', 'F'].map(g => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Operational Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Scope 1 Emissions (tCO2e)</Label>
                  <Input value={newAssessment.scope1_emissions} onChange={(e) => setNewAssessment({ ...newAssessment, scope1_emissions: e.target.value })} type="number" disabled={isSubmitting} />
                </div>
                <div>
                  <Label>Scope 2 Emissions (tCO2e)</Label>
                  <Input value={newAssessment.scope2_emissions} onChange={(e) => setNewAssessment({ ...newAssessment, scope2_emissions: e.target.value })} type="number" disabled={isSubmitting} />
                </div>
                <div>
                  <Label>Scope 3 Emissions (tCO2e)</Label>
                  <Input value={newAssessment.scope3_emissions} onChange={(e) => setNewAssessment({ ...newAssessment, scope3_emissions: e.target.value })} type="number" disabled={isSubmitting} />
                </div>
                <div>
                  <Label>Energy Consumption (MWh)</Label>
                  <Input value={newAssessment.energy_consumption_mwh} onChange={(e) => setNewAssessment({ ...newAssessment, energy_consumption_mwh: e.target.value })} type="number" disabled={isSubmitting} />
                </div>
                <div>
                  <Label>Water Withdrawal (m3)</Label>
                  <Input value={newAssessment.water_withdrawal_m3} onChange={(e) => setNewAssessment({ ...newAssessment, water_withdrawal_m3: e.target.value })} type="number" disabled={isSubmitting} />
                </div>
                <div>
                  <Label>Waste Generated (tons)</Label>
                  <Input value={newAssessment.waste_generated_tons} onChange={(e) => setNewAssessment({ ...newAssessment, waste_generated_tons: e.target.value })} type="number" disabled={isSubmitting} />
                </div>
              </div>

              {/* Incidents & CAP */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Incidents Count (H&S, Environmental)</Label>
                  <Input value={newAssessment.incidents_count} onChange={(e) => setNewAssessment({ ...newAssessment, incidents_count: e.target.value })} type="number" disabled={isSubmitting} />
                </div>
                <div>
                  <Label>Corrective Action Plan in Place</Label>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={newAssessment.cap_in_place}
                      onCheckedChange={(checked) => setNewAssessment({ ...newAssessment, cap_in_place: checked })}
                      disabled={isSubmitting}
                    />
                    <Input
                      placeholder="CAP due date"
                      type="date"
                      value={newAssessment.cap_due_date}
                      onChange={(e) => setNewAssessment({ ...newAssessment, cap_due_date: e.target.value })}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              {/* Custom Questions */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Custom Questions</Label>
                </div>
                {customQuestions.length > 0 && (
                  <div className="space-y-3">
                    {customQuestions.map((q, idx) => (
                      <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                        <div>
                          <Label>{q.label}{q.required ? ' *' : ''}</Label>
                          {q.type === 'text' && (
                            <Input
                              value={q.value || ''}
                              onChange={(e) => {
                                const arr = [...customQuestions];
                                arr[idx].value = e.target.value;
                                setCustomQuestions(arr);
                              }}
                              disabled={isSubmitting}
                            />
                          )}
                          {q.type === 'number' && (
                            <Input
                              type="number"
                              value={q.value || ''}
                              onChange={(e) => {
                                const arr = [...customQuestions];
                                arr[idx].value = e.target.value;
                                setCustomQuestions(arr);
                              }}
                              disabled={isSubmitting}
                            />
                          )}
                          {q.type === 'date' && (
                            <Input
                              type="date"
                              value={q.value || ''}
                              onChange={(e) => {
                                const arr = [...customQuestions];
                                arr[idx].value = e.target.value;
                                setCustomQuestions(arr);
                              }}
                              disabled={isSubmitting}
                            />
                          )}
                          {q.type === 'boolean' && (
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={!!q.value}
                                onCheckedChange={(checked) => {
                                  const arr = [...customQuestions];
                                  arr[idx].value = checked;
                                  setCustomQuestions(arr);
                                }}
                                disabled={isSubmitting}
                              />
                              <span>{q.value ? 'Yes' : 'No'}</span>
                            </div>
                          )}
                          {q.type === 'select' && (
                            <Select
                              value={q.value || ''}
                              onValueChange={(value) => {
                                const arr = [...customQuestions];
                                arr[idx].value = value;
                                setCustomQuestions(arr);
                              }}
                              disabled={isSubmitting}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {(q.options || '').split(',').map((opt: string) => opt.trim()).filter(Boolean).map((opt: string) => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div className="md:col-span-2 flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              const arr = [...customQuestions];
                              arr.splice(idx, 1);
                              setCustomQuestions(arr);
                            }}
                            disabled={isSubmitting}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new question */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label>Question Label</Label>
                    <Input value={newQuestion.label} onChange={(e) => setNewQuestion({ ...newQuestion, label: e.target.value })} disabled={isSubmitting} />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={newQuestion.type} onValueChange={(value) => setNewQuestion({ ...newQuestion, type: value })} disabled={isSubmitting}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                        <SelectItem value="date">Date</SelectItem>
                        <SelectItem value="boolean">Yes/No</SelectItem>
                        <SelectItem value="select">Select</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Options (comma separated)</Label>
                    <Input value={newQuestion.options} onChange={(e) => setNewQuestion({ ...newQuestion, options: e.target.value })} placeholder="For select type" disabled={isSubmitting} />
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={newQuestion.required}
                        onCheckedChange={(checked) => setNewQuestion({ ...newQuestion, required: checked })}
                        disabled={isSubmitting}
                      />
                      <Label>Required</Label>
                    </div>
                    <Button
                      type="button"
                      onClick={() => {
                        if (!newQuestion.label) return;
                        setCustomQuestions([...customQuestions, { ...newQuestion, value: '' }]);
                        setNewQuestion({ label: '', type: 'text', options: '', required: false });
                      }}
                      disabled={isSubmitting}
                    >
                      Add Question
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsAddingAssessment(false)} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : editingAssessmentId ? 'Update Assessment' : 'Create Assessment'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {assessments?.map((assessment) => (
          <Card key={assessment.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">
                    {assessment.suppliers?.supplier_name || 'Unknown Supplier'}
                  </CardTitle>
                  <p className="text-sm text-gray-600 capitalize">
                    {assessment.assessment_type.replace('_', ' ')} Assessment
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={getStatusBadge(assessment.status)}>
                    {assessment.status?.replace('_', ' ') || 'Unknown'}
                  </Badge>
                  {(assessment as any).validation_status && (
                    <Badge variant="outline">{(assessment as any).validation_status}</Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {assessment.overall_score || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600">Overall</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {assessment.environmental_score || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600">Environmental</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {assessment.social_score || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600">Social</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {assessment.governance_score || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600">Governance</div>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {new Date(assessment.assessment_date).toLocaleDateString()}
                </div>
                {assessment.assessor_name && (
                  <div className="flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    {assessment.assessor_name}
                  </div>
                )}
                {assessment.follow_up_date && (
                  <div className="flex items-center gap-1">
                    <FileText className="h-4 w-4" />
                    Follow-up: {new Date(assessment.follow_up_date).toLocaleDateString()}
                  </div>
                )}
              </div>

              {assessment.findings && (
                <div className="mb-3">
                  <h4 className="font-medium mb-1">Key Findings:</h4>
                  <p className="text-sm text-gray-700">{assessment.findings}</p>
                </div>
              )}

              {assessment.recommendations && (
                <div className="mb-3">
                  <h4 className="font-medium mb-1">Recommendations:</h4>
                  <p className="text-sm text-gray-700">{assessment.recommendations}</p>
                </div>
              )}

              {assessment.action_items && (
                <div>
                  <h4 className="font-medium mb-1">Action Items:</h4>
                  <p className="text-sm text-gray-700">{assessment.action_items}</p>
                </div>
              )}

              <div className="flex justify-end gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => setViewAssessment(assessment)}>
                  View
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleEditAssessment(assessment)}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDeleteAssessment(assessment.id)}
                  disabled={deleteAssessmentMutation.isPending}
                >
                  {deleteAssessmentMutation.isPending ? 'Deleting...' : 'Delete'}
                </Button>
                {isAdmin && ((assessment as any).validation_status !== 'approved') && (
                  <>
                    <Button size="sm" onClick={async () => {
                      // TODO: Implement approve functionality for admin
                      toast({
                        title: "Admin Function",
                        description: "Approve functionality to be implemented",
                      });
                    }}>
                      Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={async () => {
                      // TODO: Implement reject functionality for admin
                      toast({
                        title: "Admin Function",
                        description: "Reject functionality to be implemented",
                      });
                    }}>
                      Reject
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {!assessments?.length && (
          <Card>
            <CardContent className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Assessments Yet</h3>
              <p className="text-gray-600 mb-4">
                Start by creating your first supply chain assessment.
              </p>
              <Button onClick={() => setIsAddingAssessment(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Assessment
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this assessment? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setAssessmentToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};